import type { CompareResponse, CreateRatingRequest } from '@/types/compare'
import { CompareRatingControls } from '@/components/compare/CompareRatingControls'
import { cn } from '@/lib/utils'

interface CompareCardProps {
  response: CompareResponse
  className?: string
  onSubmitRating: (request: CreateRatingRequest) => Promise<void>
}

export function CompareCard({ response, className, onSubmitRating }: CompareCardProps) {
  const isError = response.status === 'error'
  const isStreaming = response.status === 'streaming' || response.status === 'pending'

  return (
    <article
      className={cn(
        'flex h-full flex-col rounded-xl border border-border bg-card p-3 shadow-sm',
        className,
      )}
      data-testid={`compare-card-${response.modelResponseId}`}
    >
      <header className="space-y-1 border-b border-border pb-2">
        <h3 className="font-semibold">{response.model.displayName}</h3>
        <p className="text-xs text-muted-foreground">
          {response.provider.name}
          {response.category ? ` · ${response.category.name}` : ''}
        </p>
        <p className="font-mono text-[11px] text-muted-foreground">{response.settingsBadge}</p>
        {!isStreaming && !isError ? (
          <p
            className="text-xs text-muted-foreground"
            data-testid={`compare-cost-latency-${response.modelResponseId}`}
          >
            {response.tokensInput ?? 0} in / {response.tokensOutput ?? 0} out · ~$
            {(response.estimatedCost ?? 0).toFixed(6)} · {response.latencyMs ?? 0}ms
          </p>
        ) : null}
      </header>

      <div className="min-h-0 flex-1 overflow-auto py-3">
        {isError ? (
          <p className="text-sm text-destructive">{response.errorMessage ?? 'Model failed'}</p>
        ) : (
          <pre className="whitespace-pre-wrap text-sm" data-testid="compare-response-text">
            {response.normalizedText}
            {isStreaming ? (
              <span
                className="ml-0.5 inline-block animate-pulse"
                data-testid="compare-streaming-cursor"
              >
                ▍
              </span>
            ) : null}
          </pre>
        )}
      </div>

      {!isError && !isStreaming ? (
        <CompareRatingControls
          modelResponseId={response.modelResponseId}
          initialRating={response.rating}
          onSubmit={onSubmitRating}
        />
      ) : null}
    </article>
  )
}
